import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Plus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, imgUrl } from "../../lib/api";
import { useLang } from "../../i18n";

const EMPTY_COURSE = {
  title_it: "", title_en: "", desc_it: "", desc_en: "", duration: "", level: "Principiante",
  price: 0, includes_it: "", includes_en: "", image: "", datesText: "", seats: 10,
};

export default function CourseForm() {
  const { t } = useLang();
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_COURSE);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(!!id);

  useEffect(() => {
    if (!id) return;
    api.get("/courses").then((r) => {
      const c = r.data.find((x) => x.id === id);
      if (c) setForm({ ...EMPTY_COURSE, ...c, datesText: (c.next_dates || []).join(", ") });
      else toast.error("Corso non trovato");
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: data.path }));
      toast.success("Foto caricata ✓");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title_it: form.title_it, title_en: form.title_en || form.title_it,
        desc_it: form.desc_it, desc_en: form.desc_en,
        duration: form.duration, level: form.level, price: parseFloat(form.price) || 0,
        includes_it: form.includes_it, includes_en: form.includes_en,
        image: form.image, seats: Number(form.seats) || 0,
        next_dates: form.datesText.split(",").map((d) => d.trim()).filter(Boolean),
      };
      if (id) await api.put(`/admin/courses/${id}`, payload);
      else await api.post("/admin/courses", payload);
      toast.success("✓");
      navigate("/admin/corsi");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const inputCls = "w-full bg-obsidian border border-slate-700 px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-gold";
  const labelCls = "block font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-1.5";

  if (loading) return <p className="font-mono text-sm text-slate-400">{t("common.loading")}</p>;

  return (
    <div className="max-w-3xl" data-testid="course-form-page">
      <Link to="/admin/corsi" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-gold transition-colors" data-testid="cf-back-link">
        <ArrowLeft size={14} /> {t("admin.courses")}
      </Link>
      <h1 className="mt-4 font-serif text-3xl font-bold" data-testid="course-form-title">{id ? t("admin.edit") : "Nuovo corso"}</h1>
      <form onSubmit={save} className="mt-8 bg-slate2 border border-slate-700 rounded-lg p-7 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5">
        <div>
          <label className={labelCls}>Titolo (IT) *</label>
          <input required value={form.title_it} onChange={set("title_it")} placeholder="Es. Corso base di apicoltura" data-testid="cf-title-it" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Title (EN)</label>
          <input value={form.title_en} onChange={set("title_en")} placeholder="E.g. Beginner beekeeping course" data-testid="cf-title-en" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Descrizione (IT)</label>
          <textarea value={form.desc_it} onChange={set("desc_it")} rows={2} data-testid="cf-desc-it" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Description (EN)</label>
          <textarea value={form.desc_en} onChange={set("desc_en")} rows={2} data-testid="cf-desc-en" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Durata</label>
          <input value={form.duration} onChange={set("duration")} placeholder="Es. 1 giorno — 4 ore" data-testid="cf-duration" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Livello</label>
          <select value={form.level} onChange={set("level")} data-testid="cf-level" className={inputCls}>
            {["Principiante", "Intermedio", "Avanzato", "Famiglie"].map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Prezzo (€)</label>
          <input type="number" step="0.01" value={form.price} onChange={set("price")} data-testid="cf-price" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Posti disponibili</label>
          <input type="number" value={form.seats} onChange={set("seats")} data-testid="cf-seats" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Include (IT)</label>
          <input value={form.includes_it} onChange={set("includes_it")} placeholder="Es. Tuta fornita, degustazione" data-testid="cf-includes-it" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Includes (EN)</label>
          <input value={form.includes_en} onChange={set("includes_en")} placeholder="E.g. Suit provided, tasting" data-testid="cf-includes-en" className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Prossime date (separate da virgola)</label>
          <input value={form.datesText} onChange={set("datesText")} placeholder="2026-09-05, 2026-10-03" data-testid="cf-dates" className={inputCls} />
        </div>
        <div className="sm:col-span-2 space-y-2">
          <label className={labelCls}>Foto corso</label>
          <div className="flex items-center gap-4">
            {form.image && <img src={imgUrl(form.image)} alt="" className="w-16 h-16 object-cover rounded border border-slate-700" data-testid="cf-image-preview" />}
            <label className="flex-1 flex items-center justify-center gap-2 border border-dashed border-slate-600 px-4 py-3 text-sm text-slate-300 cursor-pointer hover:border-gold hover:text-gold transition-colors" data-testid="cf-upload-label">
              <Plus size={15} /> {uploading ? "Caricamento…" : "Carica foto dal tuo computer"}
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="cf-image-file" />
            </label>
          </div>
          <input value={form.image} onChange={set("image")} placeholder="oppure incolla URL immagine" data-testid="cf-image" className={inputCls} />
        </div>
        <div className="sm:col-span-2 flex gap-3 pt-2">
          <button type="submit" data-testid="cf-save" className="flex-1 bg-gold text-obsidian py-3 font-semibold hover:bg-honey transition-colors">{t("admin.save")}</button>
          <Link to="/admin/corsi" data-testid="cf-cancel" className="px-6 py-3 border border-slate-600 text-slate-300 hover:border-slate-400 transition-colors text-center">{t("admin.cancel")}</Link>
        </div>
      </form>
    </div>
  );
}
